import {Component, ElementRef, HostBinding, HostListener, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {cloneProductionScheduleElement} from '@application/helper/production-schedule-builder/clone-production-schedule-element';
import {getClosestProductionScheduleItemAndIndexRelativeToMouseCoordinates} from '@application/helper/production-schedule-builder/get-closest-production-schedule-item-and-index-relative-to-mouse-coordinates';
import {getMouseCoordinatesInProductionScheduleBuilderGroup} from '@application/helper/production-schedule-builder/get-mouse-coordinates-in-production-schedule-builder-group';
import {getUnderlyingElement} from '@application/helper/production-schedule-builder/get-underlying-element';
import {isCoordinateOutOfRangeOfOtherCoordinate} from '@application/helper/production-schedule-builder/is-coordinate-out-of-range-of-other-coordinate';
import {isInLowerHalfOfProductionScheduleItem} from '@application/helper/production-schedule-builder/is-in-lower-half-of-production-schedule-item';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {Coordinates} from '@domain/coordinates';
import {ClosestProductionScheduleItemInPathGroup} from '@domain/production-schedule/closest-production-schedule-item-in-path-group';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {Drawing} from '@domain/production-schedule/drawing';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {ProductionScheduleBuilderAspectRatio} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-builder-aspect-ratio';
import {EditProductionScheduleItemInPathGroupAction} from '@presentation/pages/texfab/production-schedule/add/plan/modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group-action';
import {EditProductionScheduleItemInPathGroupComponent} from '@presentation/pages/texfab/production-schedule/add/plan/modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {AssertionUtils, DialogBuilderFactoryService, Unit} from '@vdw/angular-component-library';
import {D3DragEvent, drag as d3Drag} from 'd3-drag';
import {BaseType, ContainerElement, pointer as d3Pointer, select as d3Select, selectAll as d3SelectAll, Selection as d3Selection} from 'd3-selection';
import {isEqual, isNil, throttle} from 'lodash-es';
import {filter} from 'rxjs/operators';
import {ProductionScheduleItemInPathGroupRepeatIndicatorComponent} from './production-schedule-item-in-path-group-repeat-indicator/production-schedule-item-in-path-group-repeat-indicator.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-production-schedule-item-in-path-group-container]',
  templateUrl: './production-schedule-item-in-path-group-container.component.html',
  styleUrls: ['./production-schedule-item-in-path-group-container.component.scss']
})
export class ProductionScheduleItemInPathGroupContainerComponent implements OnInit, OnChanges {
  public static isDraggingProductionScheduleItemInPathGroup = false;
  @Input() public productionScheduleItem: ProductionScheduleItemInPathGroup;
  @Input() public productionScheduleItemIndexInPath: number;
  @Input() public productionScheduleBuilderHeight: number;
  @Input() public commercialUnit: Unit;
  @Input() public aspectRatio: ProductionScheduleBuilderAspectRatio;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionSchedulePathIndex: number;
  @Input() public productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public isProductionScheduleDisplayOverview: boolean;
  @HostBinding('class') public readonly labelForProductionScheduleItem = 'production-schedule-item';
  @HostBinding('attr.y') public verticalPosition: number;
  @HostBinding('attr.height') public totalHeight: number;

  public cutbarBeforeHeight: number;
  public cutbarAfterHeight: number;
  public drawingImageHeight: number;
  public drawingHeight: number;
  public drawingWidth: number;
  public isHoveringItemInPathGroup = false;

  private mouseCoordinatesOfDragStart: Coordinates;
  private dragInitializedOnCurrentProductionScheduleItem = false;
  private sourceOfDraggedProductionScheduleItemInPathGroup: d3Selection<BaseType, ProductionSchedulePath, BaseType, undefined>;
  private destinationOfDraggedProductionScheduleItem: d3Selection<BaseType, ProductionSchedulePath | OverviewListColoredYarnSetWithStartDent | ProductionScheduleItemInPathGroup, BaseType, undefined>;

  private readonly labelForProductionScheduleBuilderGroup = 'production-schedule-builder-group';
  private readonly labelForProductionScheduleColoredYarnSet = 'production-schedule-colored-yarn-set';
  private readonly labelForProductionSchedulePath = 'production-schedule-path-of-colored-yarn-set';
  private readonly labelForDraggedProductionScheduleItemElement = 'dragged-production-schedule-element';
  private readonly labelForProductionScheduleItemRepetition = 'production-schedule-item-repetition';
  private readonly dropIndicatorPositionHeight = 8;
  private readonly MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX = 1;

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly zone: NgZone,
    private readonly draggingService: DraggingService,
    public readonly elementRef: ElementRef
  ) {}

  @HostListener('mouseenter')
  public onProductionScheduleItemMouseEnter(): void {
    if (!ProductionScheduleItemInPathGroupContainerComponent.isDraggingProductionScheduleItemInPathGroup && this.canEditProductionScheduleItem()) {
      this.isHoveringItemInPathGroup = true;
    }
  }

  @HostListener('mouseleave')
  public onProductionScheduleItemMouseLeave(): void {
    this.isHoveringItemInPathGroup = false;
  }

  public ngOnInit(): void {
    d3Select(this.elementRef.nativeElement).datum(this.productionScheduleItem);
    this.setProductionScheduleItemInPathGroupDragEventHandlers();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('aspectRatio' in changes || 'productionSchedule' in changes || 'productionScheduleBuilderHeight' in changes) {
      if (this.isProductionScheduleDisplayOverview) {
        this.setHeightDimensionsForOverviewViewMode();
      } else {
        this.setHeightDimensionsForDetailViewMode();
      }

      this.drawingWidth = isNaN(this.aspectRatio.width) ? 0 : this.productionScheduleItem.technicalDimensions.widthInDents * this.aspectRatio.width;

      this.setVerticalPosition();
    }
  }

  public openEditProductionScheduleItemDialog(event: MouseEvent): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withWidth('334px')
      .withClass('edit-drawing-in-path')
      .openDialog(EditProductionScheduleItemInPathGroupComponent, {
        productionScheduleItemInPathGroup: this.productionScheduleItem,
        commercialUnit: this.commercialUnit,
        productionSchedule: this.productionSchedule,
        matDialogPosition: {
          y: event.clientY,
          x: event.clientX
        },
        allowExceedingRequestedAmountOfOrderLineItems: this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems,
        pickDensityInPicksPerMeter: this.productionSchedule.machineQuality.pickDensity * 1000
      })
      .pipe(filter((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => !isNil(result)))
      .subscribe((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => {
        if (result.action === EditProductionScheduleItemInPathGroupAction.EDIT) {
          this.editProductionScheduleItem(result.value);
        } else {
          this.removeProductionScheduleItemInPathGroup();
        }
      });
  }

  public canShowRepeatIndicator(): boolean {
    return this.productionScheduleItem.quantity > 1;
  }

  public static getCutbarHeight(cutbar: {commercialLengthInMM: number; technicalLengthInNumberOfPicks: number}, minimumHeight: number, aspectRatioHeight: number): number {
    if (!cutbar.commercialLengthInMM || !cutbar.technicalLengthInNumberOfPicks) {
      return 0;
    } else {
      const proportionalCutbarHeight = cutbar.technicalLengthInNumberOfPicks * aspectRatioHeight;
      return Math.max(proportionalCutbarHeight, minimumHeight);
    }
  }

  private setHeightDimensionsForDetailViewMode(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionScheduleItem.getCutbarInformation())) {
      const {cutbarBefore, cutbarAfter} = this.productionScheduleItem.getCutbarInformation();

      this.cutbarBeforeHeight = ProductionScheduleItemInPathGroupContainerComponent.getCutbarHeight(cutbarBefore, this.MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX, this.aspectRatio.height);
      this.cutbarAfterHeight = ProductionScheduleItemInPathGroupContainerComponent.getCutbarHeight(cutbarAfter, this.MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX, this.aspectRatio.height);
    }
    this.drawingHeight = this.productionScheduleItem.technicalDimensions.heightInPicks * this.aspectRatio.height;
    this.drawingImageHeight = this.drawingHeight;
    this.totalHeight = (this.cutbarAfterHeight + this.cutbarBeforeHeight + this.drawingHeight) * this.productionScheduleItem.quantity;
  }

  private setHeightDimensionsForOverviewViewMode(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionScheduleItem.getCutbarInformation())) {
      const {cutbarBefore, cutbarAfter} = this.productionScheduleItem.getCutbarInformation();

      this.cutbarBeforeHeight = ProductionScheduleItemInPathGroupContainerComponent.getCutbarHeight(cutbarBefore, this.MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX, this.aspectRatio.height);
      this.cutbarAfterHeight = ProductionScheduleItemInPathGroupContainerComponent.getCutbarHeight(cutbarAfter, this.MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX, this.aspectRatio.height);
    }
    this.drawingHeight = this.productionScheduleItem.technicalDimensions.heightInPicks * this.aspectRatio.height;
    this.drawingImageHeight = this.drawingHeight;
    this.totalHeight = (this.cutbarAfterHeight + this.cutbarBeforeHeight + this.drawingHeight) * this.productionScheduleItem.quantity;

    let requiredHeightForSingleProductionScheduleItemRepetition = this.cutbarAfterHeight + this.cutbarBeforeHeight + this.drawingHeight;

    if (this.productionScheduleItem.quantity > 1) {
      requiredHeightForSingleProductionScheduleItemRepetition += ProductionScheduleItemInPathGroupRepeatIndicatorComponent.minimumAllowedHeight;
    }

    if (requiredHeightForSingleProductionScheduleItemRepetition > this.totalHeight) {
      this.drawingImageHeight = Math.max(0, this.drawingImageHeight - (requiredHeightForSingleProductionScheduleItemRepetition - this.totalHeight));
      this.drawingHeight = Math.max(0, this.drawingHeight - (requiredHeightForSingleProductionScheduleItemRepetition - this.totalHeight));
    }
  }

  private editProductionScheduleItem(productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup): void {
    this.productionScheduleItem = productionScheduleItemInPathGroup;

    this.productionSchedulePlan.editProductionScheduleItem(
      productionScheduleItemInPathGroup,
      this.productionScheduleItemIndexInPath,
      this.productionSchedulePathIndex,
      this.productionScheduleColoredYarnSet.startDent
    );
  }

  private removeProductionScheduleItemInPathGroup(): void {
    const productionSchedulePathOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(this.productionScheduleColoredYarnSet.startDent);
    const productionSchedulePath = productionSchedulePathOfColoredYarnSet.productionSchedulePaths[this.productionSchedulePathIndex];

    this.productionSchedulePlan.removeProductionScheduleItem(
      this.productionScheduleItem.uuid,
      productionSchedulePath.pathNumber,
      productionSchedulePathOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );
  }

  private canEditProductionScheduleItem(): boolean {
    return this.productionScheduleItem.isRecolored && this.productionSchedulePhase !== ProductionSchedulePhase.VERIFY;
  }

  private setProductionScheduleItemInPathGroupDragEventHandlers(): void {
    const productionScheduleItemInPathGroupElement = this.elementRef.nativeElement;

    d3Select(productionScheduleItemInPathGroupElement).call(
      d3Drag()
        .on('start', (event: D3DragEvent<SVGGraphicsElement, ProductionScheduleItemInPathGroup[], ProductionScheduleItemInPathGroup>) => this.onDragStart(event.sourceEvent))
        .on('drag', (event: D3DragEvent<SVGGraphicsElement, ProductionScheduleItemInPathGroup[], ProductionScheduleItemInPathGroup>) => this.onDrag(event.sourceEvent))
        .on('end', (event: D3DragEvent<SVGGraphicsElement, ProductionScheduleItemInPathGroup[], ProductionScheduleItemInPathGroup>) => this.onDragEndProductionScheduleItem(event.sourceEvent))
    );

    this.zone.runOutsideAngular(() => {
      d3Select(productionScheduleItemInPathGroupElement)
        .on(
          'dragover',
          throttle((event: DragEvent) => this.draggedNewDrawingOverProductionScheduleItem(event), 50)
        )
        .on('drop', (event: DragEvent) => this.droppedNewDrawingOnProductionScheduleItem(event));
    });
  }

  private onDragStart(event: DragEvent): void {
    this.mouseCoordinatesOfDragStart = getMouseCoordinatesInProductionScheduleBuilderGroup(event);
  }

  private onDrag(event: DragEvent): void {
    const {x: mouseCoordinateX, y: mouseCoordinateY} = this.getMouseCoordinatesInProductionSchedulePlanView(event);

    const hasDraggedFarEnoughFromStartingPoint: boolean = isCoordinateOutOfRangeOfOtherCoordinate(
      {
        x: mouseCoordinateX,
        y: mouseCoordinateY
      },
      this.mouseCoordinatesOfDragStart
    );
    if (hasDraggedFarEnoughFromStartingPoint || this.dragInitializedOnCurrentProductionScheduleItem) {
      if (this.dragInitializedOnCurrentProductionScheduleItem) {
        const productionScheduleItemInPathGroupSelection = d3Select(this.elementRef.nativeElement);

        productionScheduleItemInPathGroupSelection.attr('x', mouseCoordinateX).attr('y', mouseCoordinateY);

        this.onDragProductionScheduleItem(event);
      } else {
        this.onDragInitializeProductionScheduleItemInPathGroup(event);
      }
    }
  }

  private onDragProductionScheduleItem(event: DragEvent): void {
    this.removeDropIndicator();

    const destinationOfDraggedProductionScheduleItem = getUnderlyingElement(
      {x: event.x, y: event.y},
      `.${this.labelForProductionScheduleItem}, .${this.labelForProductionSchedulePath}, .${this.labelForProductionScheduleColoredYarnSet}`
    );

    if (!isNil(destinationOfDraggedProductionScheduleItem)) {
      const destinationHasChanged = !isEqual(this.destinationOfDraggedProductionScheduleItem, destinationOfDraggedProductionScheduleItem);
      if (destinationHasChanged) {
        this.destinationOfDraggedProductionScheduleItem = destinationOfDraggedProductionScheduleItem;
      }

      const destinationOfDraggedProductionScheduleItemElement = this.destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement;
      const destinationOfDraggedProductionScheduleItemClassList = destinationOfDraggedProductionScheduleItemElement.classList;

      if (destinationOfDraggedProductionScheduleItemClassList.contains(this.labelForProductionScheduleItem)) {
        this.handleDragOverProductionScheduleItem(event, destinationOfDraggedProductionScheduleItemElement);
      } else if (destinationOfDraggedProductionScheduleItemClassList.contains(this.labelForProductionSchedulePath)) {
        this.handleDragOverProductionSchedulePath(event, destinationOfDraggedProductionScheduleItemElement);
      } else if (destinationOfDraggedProductionScheduleItemClassList.contains(this.labelForProductionScheduleColoredYarnSet)) {
        this.handleDragOverProductionScheduleColoredYarnSet(destinationOfDraggedProductionScheduleItemElement);
      }
    }
  }

  private onDragInitializeProductionScheduleItemInPathGroup(event: DragEvent): void {
    this.dragInitializedOnCurrentProductionScheduleItem = true;
    const productionScheduleWrapperElement = d3Select(`.${this.labelForProductionScheduleBuilderGroup}`).node() as ContainerElement;
    const [mouseCoordinateX, mouseCoordinateY] = d3Pointer(event, productionScheduleWrapperElement);
    const productionScheduleItemInPathGroupSelection = d3Select(this.elementRef.nativeElement);
    const productionScheduleItemInPathGroupElement: SVGElement = productionScheduleItemInPathGroupSelection.node();

    this.sourceOfDraggedProductionScheduleItemInPathGroup = d3Select(productionScheduleItemInPathGroupElement.parentElement);
    cloneProductionScheduleElement(this.elementRef, mouseCoordinateX);
    productionScheduleItemInPathGroupSelection.attr('y', mouseCoordinateY);
    ProductionScheduleItemInPathGroupContainerComponent.isDraggingProductionScheduleItemInPathGroup = true;
    this.isHoveringItemInPathGroup = false;
  }

  private onDragEndProductionScheduleItem(event: DragEvent): void {
    const {x: mouseCoordinateX, y: mouseCoordinateY} = this.getMouseCoordinatesInProductionSchedulePlanView(event);

    const hasDraggedFarEnoughFromStartingPoint: boolean =
      !isNil(this.mouseCoordinatesOfDragStart) &&
      isCoordinateOutOfRangeOfOtherCoordinate(
        {
          x: mouseCoordinateX,
          y: mouseCoordinateY
        },
        this.mouseCoordinatesOfDragStart
      );

    const destinationOfDraggedProductionScheduleItemElement = this.destinationOfDraggedProductionScheduleItem?.node() as SVGGraphicsElement;
    const destinationOfDraggedProductionScheduleItemClassList = destinationOfDraggedProductionScheduleItemElement?.classList;
    if (hasDraggedFarEnoughFromStartingPoint && !isNil(this.destinationOfDraggedProductionScheduleItem)) {
      const sourceProductionSchedulePath: ProductionSchedulePath = this.sourceOfDraggedProductionScheduleItemInPathGroup.datum();

      if (destinationOfDraggedProductionScheduleItemClassList.contains(this.labelForProductionScheduleItem)) {
        this.onDragEndOnProductionScheduleItem(event, sourceProductionSchedulePath, destinationOfDraggedProductionScheduleItemElement);
      } else if (destinationOfDraggedProductionScheduleItemClassList.contains(this.labelForProductionSchedulePath)) {
        this.onDragEndOnProductionSchedulePath(event, sourceProductionSchedulePath);
      } else if (destinationOfDraggedProductionScheduleItemClassList.contains(this.labelForProductionScheduleColoredYarnSet)) {
        this.ondDragEndOnColoredYarnSet(event, sourceProductionSchedulePath);
      }
    }

    const hasDroppedOnItself = destinationOfDraggedProductionScheduleItemClassList?.contains(this.labelForDraggedProductionScheduleItemElement);

    this.cleanProductionSchedulePlanAfterDrop();

    if (hasDroppedOnItself) {
      this.onProductionScheduleItemMouseEnter();
    }
  }

  private onDragEndOnProductionScheduleItem(event: DragEvent, sourceProductionSchedulePath: ProductionSchedulePath, destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement): void {
    const destinationProductionSchedulePathElement: SVGGraphicsElement = (this.destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement).parentNode as SVGGraphicsElement;
    const destinationProductionSchedulePath: ProductionSchedulePath = d3Select(destinationProductionSchedulePathElement).datum() as ProductionSchedulePath;

    const destinationPathElementSelection = d3Select(destinationProductionSchedulePathElement);
    const productionSchedulePathPositionEnd = Number(destinationPathElementSelection.attr('width'));
    const productionSchedulePathDropThreshold = this.getProductionSchedulePathDropThreshold();

    const mouseCoordinatesInProductionSchedulePath: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event, destinationProductionSchedulePathElement);

    const isDraggingInsideProductionSchedulePath: boolean =
      mouseCoordinatesInProductionSchedulePath.x > productionSchedulePathDropThreshold &&
      mouseCoordinatesInProductionSchedulePath.x < productionSchedulePathPositionEnd - productionSchedulePathDropThreshold;

    if (isDraggingInsideProductionSchedulePath) {
      const targetIndexOfDraggedProductionScheduleItem: number = this.getTargetIndexOfDestinationProductionScheduleItem(
        event,
        destinationOfDraggedProductionScheduleItemElement,
        false,
        this.productionScheduleItem
      );

      this.moveDraggedProductionScheduleItemToProductionSchedulePath(sourceProductionSchedulePath, destinationProductionSchedulePath, targetIndexOfDraggedProductionScheduleItem);
    } else {
      const dropDestination: OverviewListColoredYarnSetWithStartDent = d3Select(destinationProductionSchedulePathElement.parentElement).datum() as OverviewListColoredYarnSetWithStartDent;

      this.moveDraggedProductionScheduleItemToColoredYarnSet(event, sourceProductionSchedulePath, dropDestination, destinationProductionSchedulePath, destinationProductionSchedulePathElement);
    }
  }

  private moveDraggedProductionScheduleItemToProductionSchedulePath(
    sourceProductionSchedulePath: ProductionSchedulePath,
    destinationProductionSchedulePath: ProductionSchedulePath,
    targetIndexOfDraggedProductionScheduleItemInPathGroup?: number
  ): void {
    const canMoveProductionScheduleItemToProductionSchedulePath: boolean = this.productionSchedule.canMoveProductionScheduleItemToProductionSchedulePath(
      this.productionScheduleItemIndexInPath,
      sourceProductionSchedulePath.pathNumber,
      null,
      destinationProductionSchedulePath.pathNumber,
      this.productionScheduleItem
    );

    if (canMoveProductionScheduleItemToProductionSchedulePath) {
      const sourceProductionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(this.productionScheduleColoredYarnSet.startDent);

      const destinationOfDraggedProductionScheduleItemInPathGroupElement: SVGGraphicsElement = this.destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement;
      let destinationProductionScheduleColoredYarnSet = d3Select(destinationOfDraggedProductionScheduleItemInPathGroupElement.parentElement).datum() as OverviewListColoredYarnSetWithStartDent;

      if (destinationProductionScheduleColoredYarnSet instanceof ProductionSchedulePath) {
        destinationProductionScheduleColoredYarnSet = d3Select(
          destinationOfDraggedProductionScheduleItemInPathGroupElement.parentElement.parentElement
        ).datum() as OverviewListColoredYarnSetWithStartDent;
      }

      if (destinationProductionSchedulePath instanceof ProductionScheduleItemInPathGroup) {
        const destinationProductionSchedulePathElement = destinationOfDraggedProductionScheduleItemInPathGroupElement.parentElement;
        destinationProductionSchedulePath = d3Select(destinationProductionSchedulePathElement).datum() as ProductionSchedulePath;

        destinationProductionScheduleColoredYarnSet = d3Select(destinationProductionSchedulePathElement.parentElement).datum() as OverviewListColoredYarnSetWithStartDent;
      }

      const destinationProductionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
        destinationProductionScheduleColoredYarnSet.startDent
      );

      this.productionSchedulePlan.moveProductionScheduleItemToProductionSchedulePath(
        sourceProductionSchedulePath.pathNumber,
        this.productionScheduleItemIndexInPath,
        sourceProductionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet,
        destinationProductionSchedulePath.pathNumber,
        targetIndexOfDraggedProductionScheduleItemInPathGroup,
        destinationProductionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet,
        this.productionScheduleItem
      );
    }
  }

  private moveDraggedProductionScheduleItemToColoredYarnSet(
    event: DragEvent,
    sourceProductionSchedulePath: ProductionSchedulePath,
    destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    destinationProductionSchedulePath?: ProductionSchedulePath,
    destinationProductionSchedulePathElement?: SVGGraphicsElement
  ): void {
    const sourceProductionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(this.productionScheduleColoredYarnSet.startDent);

    const canMoveProductionScheduleItemToColoredYarnSet: boolean = this.productionSchedule.canMoveProductionScheduleItemToColoredYarnSet(
      sourceProductionSchedulePath.pathNumber,
      destinationProductionScheduleColoredYarnSet.startDent,
      this.productionScheduleItem
    );

    if (canMoveProductionScheduleItemToColoredYarnSet) {
      let targetIndexInColoredYarnSet: number = null;

      if (!isNil(destinationProductionSchedulePath)) {
        targetIndexInColoredYarnSet = this.productionSchedule
          .getProductionSchedulePathsOfColoredYarnSetAtStartDent(destinationProductionScheduleColoredYarnSet.startDent)
          .productionSchedulePaths.findIndex((productionSchedulePath: ProductionSchedulePath) => {
            return productionSchedulePath.pathNumber === destinationProductionSchedulePath.pathNumber;
          });

        const sourceProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent =
          this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(sourceProductionSchedulePath);
        const sourceIndexInColoredYarnSet = this.productionSchedule
          .getProductionSchedulePathsOfColoredYarnSetAtStartDent(sourceProductionScheduleColoredYarnSet.startDent)
          .productionSchedulePaths.findIndex((productionSchedulePath: ProductionSchedulePath) => {
            return productionSchedulePath.pathNumber === sourceProductionSchedulePath.pathNumber;
          });
        const hasDroppedLastItemOfSourcePath: boolean = sourceProductionSchedulePath.pathGroups.length === 1;
        const hasDroppedBetweenPathsRightToSourcePath: boolean =
          isEqual(destinationProductionScheduleColoredYarnSet.startDent, sourceProductionScheduleColoredYarnSet.startDent) && sourceIndexInColoredYarnSet < targetIndexInColoredYarnSet;

        if (hasDroppedLastItemOfSourcePath && hasDroppedBetweenPathsRightToSourcePath) {
          targetIndexInColoredYarnSet--;
        }

        targetIndexInColoredYarnSet = this.adjustTargetPositionOfDraggedProductionScheduleItemInColoredYarnSetAccordingToMousePosition(
          destinationProductionSchedulePathElement,
          this.getMouseCoordinatesInProductionSchedulePath(event, destinationProductionSchedulePathElement),
          targetIndexInColoredYarnSet
        );
      }

      this.productionSchedulePlan.moveProductionScheduleItemToColoredYarnSet(
        sourceProductionSchedulePath.pathNumber,
        sourceProductionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet,
        destinationProductionScheduleColoredYarnSet,
        this.productionScheduleItem,
        targetIndexInColoredYarnSet
      );
    }
  }

  private adjustTargetPositionOfDraggedProductionScheduleItemInColoredYarnSetAccordingToMousePosition(
    destinationProductionSchedulePathElement: SVGGraphicsElement,
    mouseCoordinatesInProductionSchedulePath: Coordinates,
    targetIndexInColoredYarnSet: number
  ): number {
    const {width: widthOfDestinationProductionSchedulePath} = destinationProductionSchedulePathElement.getBBox();
    const horizontalCenterOfDestinationProductionSchedulePath: number = widthOfDestinationProductionSchedulePath / 2;

    const isInRightHalfOfProductionSchedulePath: boolean = mouseCoordinatesInProductionSchedulePath.x > horizontalCenterOfDestinationProductionSchedulePath;
    if (isInRightHalfOfProductionSchedulePath) {
      targetIndexInColoredYarnSet++;
    }
    return targetIndexInColoredYarnSet;
  }

  private onDragEndOnProductionSchedulePath(event: DragEvent, sourceProductionSchedulePath: ProductionSchedulePath): void {
    const destinationProductionSchedulePath: ProductionSchedulePath = this.destinationOfDraggedProductionScheduleItem.datum() as ProductionSchedulePath;
    const destinationProductionSchedulePathElement: SVGGraphicsElement = this.destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement;

    if (this.isMousePositionInsideProductionSchedulePath(event, destinationProductionSchedulePathElement)) {
      const productionScheduleItemsInPathGroup: SVGGraphicsElement[] = this.getProductionScheduleItemElementsInPath(destinationProductionSchedulePathElement);

      const closestProductionScheduleItemInPathGroup: ClosestProductionScheduleItemInPathGroup = getClosestProductionScheduleItemAndIndexRelativeToMouseCoordinates(
        this.getMouseCoordinatesInProductionSchedulePath(event, destinationProductionSchedulePathElement),
        productionScheduleItemsInPathGroup
      );

      const closestProductionScheduleItemInPathGroupElement: SVGGraphicsElement = closestProductionScheduleItemInPathGroup.element;
      let destinationIndexInProductionSchedulePath: number = closestProductionScheduleItemInPathGroup.index;

      if (
        !isNil(closestProductionScheduleItemInPathGroupElement) &&
        !isInLowerHalfOfProductionScheduleItem(this.getMouseCoordinatesInProductionSchedulePath(event, destinationProductionSchedulePathElement), closestProductionScheduleItemInPathGroupElement)
      ) {
        destinationIndexInProductionSchedulePath = destinationIndexInProductionSchedulePath + 1;
      }

      const destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement = productionScheduleItemsInPathGroup[destinationIndexInProductionSchedulePath];
      const targetIndexOfDraggedProductionScheduleItem: number = !isNil(destinationOfDraggedProductionScheduleItemElement)
        ? this.getTargetIndexOfDestinationProductionScheduleItem(event, destinationOfDraggedProductionScheduleItemElement, false, this.productionScheduleItem)
        : null;

      this.moveDraggedProductionScheduleItemToProductionSchedulePath(sourceProductionSchedulePath, destinationProductionSchedulePath, targetIndexOfDraggedProductionScheduleItem);
    } else {
      const destinationProductionScheduleColoredYarnSet = d3Select(destinationProductionSchedulePathElement.parentElement).datum() as OverviewListColoredYarnSetWithStartDent;
      this.moveDraggedProductionScheduleItemToColoredYarnSet(
        event,
        sourceProductionSchedulePath,
        destinationProductionScheduleColoredYarnSet,
        destinationProductionSchedulePath,
        destinationProductionSchedulePathElement
      );
    }
  }

  private ondDragEndOnColoredYarnSet(event: DragEvent, sourceProductionSchedulePath: ProductionSchedulePath): void {
    const destinationProductionScheduleColoredYarnSet = this.destinationOfDraggedProductionScheduleItem.datum() as OverviewListColoredYarnSetWithStartDent;

    this.moveDraggedProductionScheduleItemToColoredYarnSet(event, sourceProductionSchedulePath, destinationProductionScheduleColoredYarnSet);
  }

  private getMouseCoordinatesInProductionSchedulePlanView(event: DragEvent): Coordinates {
    const productionScheduleWrapperElement = d3Select(`.${this.labelForProductionScheduleBuilderGroup}`).node() as ContainerElement;
    const [mouseCoordinateX, mouseCoordinateY] = d3Pointer(event, productionScheduleWrapperElement);
    return {x: mouseCoordinateX, y: mouseCoordinateY};
  }

  private drawProductionSchedulePathDropIndicator(parentElement: Element, dropIndicatorPosition: number): void {
    d3Select(parentElement).append('rect').attr('class', 'production-schedule-drop-indicator vertical').attr('x', dropIndicatorPosition).raise();
  }

  private drawProductionScheduleItemDropIndicator(parentElement: Element, dropIndicatorPosition: number): void {
    dropIndicatorPosition = dropIndicatorPosition - this.dropIndicatorPositionHeight;

    d3Select(parentElement).append('rect').attr('class', 'production-schedule-drop-indicator horizontal').attr('y', dropIndicatorPosition).raise();
  }

  private removeDropIndicator(): void {
    d3SelectAll('.production-schedule-drop-indicator').remove();
  }

  private handleDragOverProductionScheduleItem(event: DragEvent, destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement): void {
    const destinationPathElement: SVGGraphicsElement = destinationOfDraggedProductionScheduleItemElement.parentElement as any as SVGGraphicsElement;

    if (this.isMousePositionInsideProductionSchedulePath(event, destinationPathElement)) {
      const isDraggingOverRepetitionOfProductionScheduleItem: boolean = d3Select(destinationOfDraggedProductionScheduleItemElement)
        .node()
        .classList.contains(this.labelForProductionScheduleItemRepetition);

      let canMoveProductionScheduleItemToProductionSchedulePath = true;

      const productionScheduleItem = this.destinationOfDraggedProductionScheduleItem.datum() as ProductionScheduleItemInPathGroup;

      if (!isNil(productionScheduleItem)) {
        const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetForProductionScheduleItem(productionScheduleItem);

        const destinationProductionSchedulePath = productionSchedulePathsOfColoredYarnSet.getProductionSchedulePathForProductionScheduleItem(productionScheduleItem);

        canMoveProductionScheduleItemToProductionSchedulePath = this.productionSchedule.canMoveProductionScheduleItemToProductionSchedulePath(
          this.productionScheduleItemIndexInPath,
          this.sourceOfDraggedProductionScheduleItemInPathGroup.datum().pathNumber,
          null,
          destinationProductionSchedulePath.pathNumber,
          this.productionScheduleItem
        );
      }

      if (!isDraggingOverRepetitionOfProductionScheduleItem && canMoveProductionScheduleItemToProductionSchedulePath) {
        const targetPositionOfDraggedProductionScheduleItemInPath: number = this.getTargetDropIndicatorPositionOfDraggedProductionScheduleItemInPath(
          event,
          destinationOfDraggedProductionScheduleItemElement,
          this.productionScheduleItem
        );

        this.drawProductionScheduleItemDropIndicator(destinationPathElement, targetPositionOfDraggedProductionScheduleItemInPath);
      }
    } else {
      const destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent = d3Select(destinationPathElement.parentElement).datum() as OverviewListColoredYarnSetWithStartDent;

      if (
        this.productionSchedule.canMoveProductionScheduleItemToColoredYarnSet(
          this.sourceOfDraggedProductionScheduleItemInPathGroup.datum().pathNumber,
          destinationProductionScheduleColoredYarnSet.startDent,
          this.productionScheduleItem
        )
      ) {
        const targetPositionOfDraggedProductionScheduleItemInColoredYarnSetPath: number = this.getTargetDropIndicatorPositionOfDraggedProductionScheduleItemInColoredYarnSetPath(
          event,
          destinationPathElement
        );
        this.drawProductionSchedulePathDropIndicator(destinationPathElement.parentElement, targetPositionOfDraggedProductionScheduleItemInColoredYarnSetPath);
      }
    }
  }

  private handleDragOverProductionSchedulePath(event: DragEvent, destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement): void {
    if (this.isMousePositionInsideProductionSchedulePath(event, destinationOfDraggedProductionScheduleItemElement)) {
      const canMoveProductionScheduleItemToProductionSchedulePath: boolean = this.productionSchedule.canMoveProductionScheduleItemToProductionSchedulePath(
        this.productionScheduleItemIndexInPath,
        this.sourceOfDraggedProductionScheduleItemInPathGroup.datum().pathNumber,
        null,
        (this.destinationOfDraggedProductionScheduleItem.datum() as ProductionSchedulePath).pathNumber,
        this.productionScheduleItem
      );

      if (canMoveProductionScheduleItemToProductionSchedulePath) {
        this.handleMousePositionInsideProductionSchedulePath(event, destinationOfDraggedProductionScheduleItemElement);
      }
    } else {
      const destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent = d3Select(
        destinationOfDraggedProductionScheduleItemElement.parentElement
      ).datum() as OverviewListColoredYarnSetWithStartDent;

      if (
        this.productionSchedule.canMoveProductionScheduleItemToColoredYarnSet(
          this.sourceOfDraggedProductionScheduleItemInPathGroup.datum().pathNumber,
          destinationProductionScheduleColoredYarnSet.startDent,
          this.productionScheduleItem
        )
      ) {
        const targetPositionOfDraggedProductionScheduleItemInColoredYarnSetPath: number = this.getTargetDropIndicatorPositionOfDraggedProductionScheduleItemInColoredYarnSetPath(
          event,
          destinationOfDraggedProductionScheduleItemElement
        );
        this.drawProductionSchedulePathDropIndicator(destinationOfDraggedProductionScheduleItemElement.parentElement, targetPositionOfDraggedProductionScheduleItemInColoredYarnSetPath);
      }
    }
  }

  private handleDragOverProductionScheduleColoredYarnSet(destinationOfDraggedProductionScheduleItemInPathGroupElement: SVGGraphicsElement): void {
    const sourceProductionSchedulePath: ProductionSchedulePath = this.sourceOfDraggedProductionScheduleItemInPathGroup.datum();
    const destinationProductionScheduleColoredYarnSet = this.destinationOfDraggedProductionScheduleItem.datum() as OverviewListColoredYarnSetWithStartDent;

    const canDropProductionScheduleItemOnToColoredYarnSet: boolean = this.productionSchedule.canMoveProductionScheduleItemToColoredYarnSet(
      sourceProductionSchedulePath.pathNumber,
      destinationProductionScheduleColoredYarnSet.startDent,
      this.productionScheduleItem
    );

    if (canDropProductionScheduleItemOnToColoredYarnSet) {
      const productionSchedulePaths = d3Select(destinationOfDraggedProductionScheduleItemInPathGroupElement).selectAll(`.${this.labelForProductionSchedulePath}`).nodes();

      const lastProductionSchedulePathSelection = d3Select(productionSchedulePaths[productionSchedulePaths.length - 1]);

      if (!isNil(lastProductionSchedulePathSelection.node())) {
        const dropIndicatorPosition = Number(lastProductionSchedulePathSelection.attr('x')) + Number(lastProductionSchedulePathSelection.attr('width'));
        this.drawProductionSchedulePathDropIndicator(destinationOfDraggedProductionScheduleItemInPathGroupElement, dropIndicatorPosition);
      }
    }
  }

  private handleMousePositionInsideProductionSchedulePath(event: DragEvent, destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement): void {
    const productionScheduleItemsInPathGroup: SVGGraphicsElement[] = this.getProductionScheduleItemElementsInPath(destinationOfDraggedProductionScheduleItemElement);
    const mouseCoordinates: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event, destinationOfDraggedProductionScheduleItemElement);

    const closestProductionScheduleItemInPathGroup: ClosestProductionScheduleItemInPathGroup = getClosestProductionScheduleItemAndIndexRelativeToMouseCoordinates(
      mouseCoordinates,
      productionScheduleItemsInPathGroup
    );
    let dropIndicatorPosition: number;
    const closestProductionScheduleItemInPathGroupElement: SVGGraphicsElement = closestProductionScheduleItemInPathGroup.element;
    let destinationIndexInProductionSchedulePath: number = closestProductionScheduleItemInPathGroup.index;

    if (!isNil(closestProductionScheduleItemInPathGroupElement)) {
      const heightOfClosestProductionScheduleItem = Number(closestProductionScheduleItemInPathGroupElement.getAttribute('height'));
      const verticalPositionOfClosestProductionScheduleItem = Number(closestProductionScheduleItemInPathGroupElement.getAttribute('y'));

      if (isInLowerHalfOfProductionScheduleItem(mouseCoordinates, closestProductionScheduleItemInPathGroupElement)) {
        dropIndicatorPosition = verticalPositionOfClosestProductionScheduleItem + heightOfClosestProductionScheduleItem;
      } else {
        dropIndicatorPosition = verticalPositionOfClosestProductionScheduleItem;
        destinationIndexInProductionSchedulePath = destinationIndexInProductionSchedulePath + 1;
      }
    } else {
      dropIndicatorPosition = Number(productionScheduleItemsInPathGroup[productionScheduleItemsInPathGroup.length - 1].getAttribute('y'));
    }

    if (isEqual(destinationIndexInProductionSchedulePath, 0)) {
      dropIndicatorPosition -= this.dropIndicatorPositionHeight;
    }

    this.drawProductionScheduleItemDropIndicator(destinationOfDraggedProductionScheduleItemElement, dropIndicatorPosition);
  }

  private getTargetDropIndicatorPositionOfDraggedProductionScheduleItemInPath(
    event: DragEvent,
    destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement,
    draggedItem: Drawing | ProductionScheduleItemInPathGroup
  ): number {
    const indexOfDestinationProductionScheduleItem: number = this.getTargetIndexOfDestinationProductionScheduleItem(event, destinationOfDraggedProductionScheduleItemElement, true, draggedItem);

    const destinationPathElement = destinationOfDraggedProductionScheduleItemElement.parentElement;
    const productionScheduleItemElementsInPath: SVGGraphicsElement[] = this.getProductionScheduleItemElementsInPath(destinationPathElement);

    const destinationProductionScheduleItemSelection = d3Select(productionScheduleItemElementsInPath[indexOfDestinationProductionScheduleItem]);
    const destinationProductionScheduleItemElement = destinationProductionScheduleItemSelection.node();
    let verticalPositionOfDropIndicator: number;

    if (isNil(destinationProductionScheduleItemElement)) {
      verticalPositionOfDropIndicator = Number(d3Select(destinationOfDraggedProductionScheduleItemElement).attr('y'));
    } else {
      verticalPositionOfDropIndicator = Math.min(
        Number(destinationProductionScheduleItemSelection.attr('y')) + Number(destinationProductionScheduleItemSelection.attr('height')),
        this.productionScheduleBuilderHeight
      );
    }

    return verticalPositionOfDropIndicator;
  }

  private getTargetIndexOfDestinationProductionScheduleItem(
    event: DragEvent,
    destinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement,
    isForShowingDropIndicator: boolean,
    draggedItem: Drawing | ProductionScheduleItemInPathGroup
  ): number {
    const mouseCoordinatesInProductionSchedulePlanView: Coordinates = this.getMouseCoordinatesInProductionSchedulePlanView(event);

    const destinationPathElement = destinationOfDraggedProductionScheduleItemElement.parentElement;
    const productionScheduleItemElementsInPath: SVGGraphicsElement[] = this.getProductionScheduleItemElementsInPath(destinationPathElement);

    let indexOfDestinationProductionScheduleItem: number;

    if (destinationOfDraggedProductionScheduleItemElement.classList.contains(this.labelForProductionScheduleItemRepetition)) {
      const repetition: ProductionScheduleItemInPathGroup = d3Select(destinationOfDraggedProductionScheduleItemElement).datum() as ProductionScheduleItemInPathGroup;
      const originalDestinationOfDraggedProductionScheduleItemElement: SVGGraphicsElement = (
        d3SelectAll(`.${this.labelForProductionScheduleItem}.production-schedule-item-${repetition.uuid}`)
          .nodes()
          .filter((svgGraphicsElement: SVGGraphicsElement) => !svgGraphicsElement.classList.contains(this.labelForProductionScheduleItemRepetition)) as SVGGraphicsElement[]
      )[0];
      indexOfDestinationProductionScheduleItem = productionScheduleItemElementsInPath.indexOf(originalDestinationOfDraggedProductionScheduleItemElement);
    } else {
      indexOfDestinationProductionScheduleItem = productionScheduleItemElementsInPath.indexOf(destinationOfDraggedProductionScheduleItemElement);
    }

    const {height: heightOfDestinationOfDraggedProductionScheduleItem} = destinationOfDraggedProductionScheduleItemElement.getBBox();
    const destinationOfDraggedProductionScheduleItemSelection = d3Select(destinationOfDraggedProductionScheduleItemElement);
    const verticalPositionOfDestinationOfDraggedProductionScheduleItem = Number(destinationOfDraggedProductionScheduleItemSelection.attr('y'));
    const verticalCenterOfProductionScheduleItem = verticalPositionOfDestinationOfDraggedProductionScheduleItem + heightOfDestinationOfDraggedProductionScheduleItem / 2;

    const otherProductionScheduleItem: ProductionScheduleItemInPathGroup = destinationOfDraggedProductionScheduleItemSelection.datum() as ProductionScheduleItemInPathGroup;
    const isDroppingOnSameProductionScheduleItem: boolean =
      draggedItem instanceof ProductionScheduleItemInPathGroup && draggedItem.uuid === (isNil(otherProductionScheduleItem) ? draggedItem.uuid : otherProductionScheduleItem.uuid);
    if (isDroppingOnSameProductionScheduleItem) {
      indexOfDestinationProductionScheduleItem = this.productionScheduleItemIndexInPath;
    } else {
      if (isForShowingDropIndicator) {
        indexOfDestinationProductionScheduleItem = this.adjustTargetPositionOfDraggedProductionScheduleItemInPathAccordingToMousePositionForDropIndicator(
          mouseCoordinatesInProductionSchedulePlanView,
          verticalCenterOfProductionScheduleItem,
          indexOfDestinationProductionScheduleItem
        );
      } else {
        indexOfDestinationProductionScheduleItem = this.adjustTargetPositionOfDraggedProductionScheduleItemInPathAccordingToMousePosition(
          mouseCoordinatesInProductionSchedulePlanView,
          verticalCenterOfProductionScheduleItem,
          destinationOfDraggedProductionScheduleItemSelection,
          indexOfDestinationProductionScheduleItem,
          draggedItem
        );
      }
    }

    return indexOfDestinationProductionScheduleItem;
  }

  private adjustTargetPositionOfDraggedProductionScheduleItemInPathAccordingToMousePositionForDropIndicator(
    mouseCoordinatesInProductionSchedulePlanView: Coordinates,
    verticalCenterOfProductionScheduleItem: number,
    indexOfDestinationProductionScheduleItem: number
  ): number {
    const isInUpperHalfOfProductionScheduleItem: boolean = mouseCoordinatesInProductionSchedulePlanView.y <= verticalCenterOfProductionScheduleItem;

    if (isInUpperHalfOfProductionScheduleItem) {
      indexOfDestinationProductionScheduleItem++;
    }
    return indexOfDestinationProductionScheduleItem;
  }

  private adjustTargetPositionOfDraggedProductionScheduleItemInPathAccordingToMousePosition(
    mouseCoordinatesInProductionSchedulePlanView: Coordinates,
    verticalCenterOfProductionScheduleItem: number,
    destinationOfDraggedProductionScheduleItemSelection: any,
    indexOfDestinationProductionScheduleItem: number,
    draggedItem: Drawing | ProductionScheduleItemInPathGroup
  ): number {
    const isInUpperHalfOfProductionScheduleItem: boolean = mouseCoordinatesInProductionSchedulePlanView.y <= verticalCenterOfProductionScheduleItem;
    const isInSameProductionSchedulePath: boolean =
      draggedItem instanceof ProductionScheduleItemInPathGroup &&
      isEqual(this.sourceOfDraggedProductionScheduleItemInPathGroup.datum(), d3Select(destinationOfDraggedProductionScheduleItemSelection.node().parentElement).datum());

    const isDestinationRepetition: ProductionScheduleItemInPathGroup = destinationOfDraggedProductionScheduleItemSelection.classed(this.labelForProductionScheduleItemRepetition);

    if (isInSameProductionSchedulePath) {
      const isDraggingHigherThanOriginalPosition: boolean = indexOfDestinationProductionScheduleItem > this.productionScheduleItemIndexInPath;

      if (isDraggingHigherThanOriginalPosition) {
        if (!isInUpperHalfOfProductionScheduleItem && !isDestinationRepetition) {
          indexOfDestinationProductionScheduleItem--;
        }
      } else {
        if (isInUpperHalfOfProductionScheduleItem) {
          indexOfDestinationProductionScheduleItem++;
        }
      }
    } else {
      if (isInUpperHalfOfProductionScheduleItem) {
        indexOfDestinationProductionScheduleItem++;
      }
    }
    return indexOfDestinationProductionScheduleItem;
  }

  private isMousePositionInsideProductionSchedulePath(event: DragEvent, destinationPathElement: SVGGraphicsElement): boolean {
    const destinationPathElementSelection = d3Select(destinationPathElement);
    const productionSchedulePathPositionEnd = Number(destinationPathElementSelection.attr('width'));

    const mouseCoordinatesInProductionSchedulePath: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event, destinationPathElementSelection.node() as ContainerElement);
    const productionSchedulePathDropThreshold = this.getProductionSchedulePathDropThreshold();

    return (
      mouseCoordinatesInProductionSchedulePath.x > productionSchedulePathDropThreshold &&
      mouseCoordinatesInProductionSchedulePath.x < productionSchedulePathPositionEnd - productionSchedulePathDropThreshold
    );
  }

  private getTargetDropIndicatorPositionOfDraggedProductionScheduleItemInColoredYarnSetPath(event: DragEvent, destinationPathElement: SVGGraphicsElement): number {
    const mouseCoordinatesInProductionSchedulePlanView: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event, destinationPathElement);

    const destinationPathElementSelection = d3Select(destinationPathElement);
    const productionSchedulePathPositionStart = Number(destinationPathElementSelection.attr('x'));
    const productionSchedulePathPositionEnd = productionSchedulePathPositionStart + Number(destinationPathElementSelection.attr('width'));
    const productionSchedulePathDropThreshold = this.getProductionSchedulePathDropThreshold();

    const isInLeftSideOfPath: boolean = mouseCoordinatesInProductionSchedulePlanView.x > 0 && mouseCoordinatesInProductionSchedulePlanView.x < productionSchedulePathDropThreshold;

    return isInLeftSideOfPath ? productionSchedulePathPositionStart : productionSchedulePathPositionEnd;
  }

  private getProductionScheduleItemElementsInPath(destinationPath: BaseType): SVGGraphicsElement[] {
    return d3Select(destinationPath)
      .selectAll(`.${this.labelForProductionScheduleItem}`)
      .nodes()
      .filter((svgGraphicsElement: SVGGraphicsElement) => !svgGraphicsElement.classList.contains(this.labelForProductionScheduleItemRepetition)) as SVGGraphicsElement[];
  }

  private getMouseCoordinatesInProductionSchedulePath(event: DragEvent, destinationProductionSchedulePathElement: ContainerElement): Coordinates {
    const [mouseCoordinateX, mouseCoordinateY] = d3Pointer(event, destinationProductionSchedulePathElement);
    return {x: mouseCoordinateX, y: mouseCoordinateY};
  }

  private cleanProductionSchedulePlanAfterDrop(): void {
    this.removeDropIndicator();
    this.restoreProductionScheduleItemClone();
    this.restoreProductionScheduleToNonDraggingState();
  }

  private restoreProductionScheduleToNonDraggingState(): void {
    this.dragInitializedOnCurrentProductionScheduleItem = false;
    ProductionScheduleItemInPathGroupContainerComponent.isDraggingProductionScheduleItemInPathGroup = false;
  }

  private restoreProductionScheduleItemClone(): void {
    const draggedProductionScheduleItem = d3SelectAll(` .${this.labelForDraggedProductionScheduleItemElement}`);

    const draggedProductionScheduleItemNode = draggedProductionScheduleItem.node() as SVGGraphicsElement;
    if (isNil(draggedProductionScheduleItemNode)) {
      d3Select('.production-schedule-element-clone').remove();
    } else {
      const productionSchedulePathOfDraggedProductionScheduleItem = draggedProductionScheduleItemNode.parentNode;

      const cloneOfDraggedProductionScheduleItem = d3Select('.production-schedule-element-clone')
        .attr('x', draggedProductionScheduleItem.attr('x'))
        .attr('y', draggedProductionScheduleItem.attr('y'))
        .attr('class', this.labelForProductionScheduleItem);

      productionSchedulePathOfDraggedProductionScheduleItem.insertBefore(
        cloneOfDraggedProductionScheduleItem.remove().node() as SVGGraphicsElement,
        (draggedProductionScheduleItem.node() as SVGGraphicsElement).nextSibling
      );
      draggedProductionScheduleItem.remove();
    }
  }

  private draggedNewDrawingOverProductionScheduleItem(dragEvent: DragEvent): void {
    dragEvent.preventDefault();

    this.removeDropIndicator();
    const destinationOfDraggedProductionScheduleItem = d3Select(this.elementRef.nativeElement) as d3Selection<
      BaseType,
      ProductionSchedulePath | OverviewListColoredYarnSetWithStartDent | ProductionScheduleItemInPathGroup,
      BaseType,
      undefined
    >;

    const destinationHasChanged = !isEqual(this.destinationOfDraggedProductionScheduleItem, destinationOfDraggedProductionScheduleItem);

    if (destinationHasChanged) {
      this.destinationOfDraggedProductionScheduleItem = destinationOfDraggedProductionScheduleItem;
    }

    const mouseCoordinatesInProductionSchedulePath: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(dragEvent, this.elementRef.nativeElement);
    const destinationProductionSchedulePathElement: SVGGraphicsElement = (destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement).parentNode as SVGGraphicsElement;
    const destinationPathElementSelection = d3Select(destinationProductionSchedulePathElement);
    const destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent = d3Select(
      destinationProductionSchedulePathElement.parentNode as SVGGraphicsElement
    ).datum() as OverviewListColoredYarnSetWithStartDent;

    const productionSchedulePathPositionEnd = Number(destinationPathElementSelection.attr('width'));
    const productionSchedulePathDropThreshold = this.getProductionSchedulePathDropThreshold();

    const hasDraggedInsideProductionSchedulePath: boolean =
      mouseCoordinatesInProductionSchedulePath.x > productionSchedulePathDropThreshold &&
      mouseCoordinatesInProductionSchedulePath.x < productionSchedulePathPositionEnd - productionSchedulePathDropThreshold;

    const destinationProductionSchedulePath = destinationPathElementSelection.datum() as ProductionSchedulePath;
    if (hasDraggedInsideProductionSchedulePath) {
      if (isEqual(dragEvent.type, 'dragover')) {
        dragEvent.stopPropagation();
      }
      if (!('quantity' in this.draggingService.getCurrentData())) {
        const draggedItem: ListDrawing = this.draggingService.getCurrentData();

        if (this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePath(draggedItem, destinationProductionSchedulePath.pathNumber, destinationProductionScheduleColoredYarnSet)) {
          const targetDropIndicatorPositionOfDestinationProductionScheduleItem = this.getTargetDropIndicatorPositionOfDraggedProductionScheduleItemInPath(
            dragEvent,
            destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement,
            draggedItem.drawing
          );

          this.drawProductionScheduleItemDropIndicator(destinationProductionSchedulePathElement, targetDropIndicatorPositionOfDestinationProductionScheduleItem);
        }
      }
    }
  }

  private droppedNewDrawingOnProductionScheduleItem(event: DragEvent): void {
    const destinationOfDraggedProductionScheduleItem = d3Select(this.elementRef.nativeElement);

    const mouseCoordinatesInProductionSchedulePath: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event, destinationOfDraggedProductionScheduleItem.node());
    const destinationProductionSchedulePathElement: SVGGraphicsElement = (destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement).parentNode as SVGGraphicsElement;
    const destinationPathElementSelection = d3Select(destinationProductionSchedulePathElement);
    const destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent = d3Select(
      destinationProductionSchedulePathElement.parentNode as SVGGraphicsElement
    ).datum() as OverviewListColoredYarnSetWithStartDent;
    const productionSchedulePathPositionEnd = Number(destinationPathElementSelection.attr('width'));
    const productionSchedulePathDropThreshold = this.getProductionSchedulePathDropThreshold();

    const hasDroppedInsideProductionSchedulePath: boolean =
      mouseCoordinatesInProductionSchedulePath.x > productionSchedulePathDropThreshold &&
      mouseCoordinatesInProductionSchedulePath.x < productionSchedulePathPositionEnd - productionSchedulePathDropThreshold;

    const destinationProductionSchedulePath = destinationPathElementSelection.datum() as ProductionSchedulePath;

    if (!('quantity' in this.draggingService.getCurrentData())) {
      const draggedItem: ListDrawing = this.draggingService.getCurrentData();

      if (
        hasDroppedInsideProductionSchedulePath &&
        this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePath(draggedItem, destinationProductionSchedulePath.pathNumber, destinationProductionScheduleColoredYarnSet)
      ) {
        const targetIndexOfDestinationProductionScheduleItem = this.getTargetIndexOfDestinationProductionScheduleItem(
          event,
          destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement,
          false,
          draggedItem.drawing
        );
        this.productionSchedulePlan.addDrawingToProductionSchedulePath(
          this.draggingService.getCurrentData(),
          destinationProductionSchedulePath.pathNumber,
          destinationProductionScheduleColoredYarnSet,
          targetIndexOfDestinationProductionScheduleItem
        );
      }
    }
  }

  private getProductionSchedulePathDropThreshold(): number {
    return 30 * this.aspectRatio.width;
  }

  private setVerticalPosition(): void {
    this.verticalPosition =
      !isNil(this.elementRef.nativeElement) && this.productionScheduleItemIndexInPath !== 0
        ? this.elementRef.nativeElement.previousElementSibling.getAttribute('y') - this.totalHeight
        : this.productionScheduleBuilderHeight - this.totalHeight;
  }
}
